import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { can, fmtDur, ldnCompose, ldnDateInput, ldnTimeInput } from '@foodstyles/shared';
import { useAuth } from '../../context/AuthContext';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useToast } from '../../context/ToastContext';
import { useUpdateEntry } from '../../hooks/useEntries';
import { Btn, Field, Modal, TextInput } from '../../components/primitives';
import { TaskCombobox } from '../../components/TaskCombobox';
import { ApiError } from '../../lib/api';
import type { EntryDto } from '../../types/api';

export function EntryEditModal({ entry, onClose }: { entry: EntryDto | null; onClose: () => void }) {
  const { user } = useAuth();
  const boot = useBootstrap();
  const toast = useToast();
  const update = useUpdateEntry();

  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [stopDate, setStopDate] = useState('');
  const [stopTime, setStopTime] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!entry) return;
    setDescription(entry.description);
    setProjectId(entry.projectId);
    setTaskId(entry.taskId);
    setStartDate(ldnDateInput(entry.start));
    setStartTime(ldnTimeInput(entry.start));
    if (entry.stop != null) {
      setStopDate(ldnDateInput(entry.stop));
      setStopTime(ldnTimeInput(entry.stop));
    } else {
      setStopDate('');
      setStopTime('');
    }
    setErr('');
  }, [entry]);

  if (!entry || !user) return null;
  const canMeta = can(user, 'editEntryMeta', { userId: entry.userId });
  const canTime = can(user, 'editEntryTime');
  const running = entry.stop == null;

  const save = () => {
    setErr('');
    const meta = canMeta ? { description, projectId, taskId } : undefined;
    let times: { start?: number; stop?: number } | undefined;
    if (canTime) {
      const start = ldnCompose(startDate, startTime);
      times = { start };
      if (!running) {
        const stop = ldnCompose(stopDate, stopTime);
        if (stop <= start) {
          setErr('Stop time must be after start time.');
          return;
        }
        times.stop = stop;
      }
    }
    if (!meta && !times) {
      onClose();
      return;
    }
    update.mutate(
      { id: entry.id, meta, times },
      {
        onSuccess: () => onClose(),
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not save changes.'),
      },
    );
  };

  const projects = boot.data?.projects ?? [];
  const tasks = boot.data?.tasks ?? [];
  const duration =
    !running && stopDate && stopTime ? fmtDur(ldnCompose(stopDate, stopTime) - ldnCompose(startDate, startTime)) : null;

  return (
    <Modal open={!!entry} onClose={onClose} title="Edit entry">
      <div className="space-y-4">
        <Field label="Description">
          <TextInput value={description} disabled={!canMeta} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Task">
          <TaskCombobox
            projects={projects}
            tasks={tasks}
            projectId={projectId}
            taskId={taskId}
            selectedLabel={entry.taskName ? [entry.projectName, entry.taskName].filter(Boolean).join(' · ') : undefined}
            disabled={!canMeta}
            onPick={({ projectId: p, taskId: t }) => {
              setProjectId(p || null);
              setTaskId(t || null);
            }}
          />
        </Field>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Time values</span>
            {!canTime && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                <Lock size={11} /> Admin only
              </span>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-12 text-xs font-semibold text-slate-500 dark:text-slate-400">Start</span>
              <TextInput type="date" className="flex-1" value={startDate} disabled={!canTime} onChange={(e) => setStartDate(e.target.value)} />
              <TextInput type="time" className="w-32" value={startTime} disabled={!canTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-12 text-xs font-semibold text-slate-500 dark:text-slate-400">Stop</span>
              <TextInput type="date" className="flex-1" value={stopDate} disabled={!canTime || running} onChange={(e) => setStopDate(e.target.value)} />
              <TextInput type="time" className="w-32" value={stopTime} disabled={!canTime || running} onChange={(e) => setStopTime(e.target.value)} />
            </div>
            {duration && <p className="text-right text-xs text-slate-400">Duration: {duration}</p>}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Editing a time value is recorded in the audit log.</p>
        </div>

        {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" onClick={save} disabled={update.isPending || (!canMeta && !canTime)}>
            Save
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
